import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

import { CreatePageModal } from "@/components/CreatePageModal";
import { PageAttachments } from "@/components/PageAttachments";
import { api } from "@/lib/api";
import type { Member, Page, PageSummary, Space } from "@/lib/types";

export function WikiPagePage() {
  const { pageId } = useParams<{ pageId: string }>();
  const [subPageModalOpen, setSubPageModalOpen] = useState(false);

  const { data: page, isLoading } = useQuery({
    queryKey: ["page", pageId],
    queryFn: async () => (await api.get<Page>(`/pages/${pageId}`)).data,
    enabled: !!pageId,
  });

  const { data: spaces = [] } = useQuery({
    queryKey: ["spaces"],
    queryFn: async () => (await api.get<Space[]>("/spaces")).data,
  });
  const space = useMemo(
    () => (page ? spaces.find((s) => s.id === page.space_id) ?? null : null),
    [page, spaces],
  );

  const { data: siblings = [] } = useQuery({
    queryKey: ["pages", space?.slug],
    queryFn: async () =>
      (await api.get<PageSummary[]>(`/spaces/${space!.slug}/pages`)).data,
    enabled: !!space,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["members"],
    queryFn: async () => (await api.get<Member[]>("/admin/members")).data,
  });
  const memberById = new Map(members.map((m) => [m.user_id, m]));

  // Walk parent chain from current page up to root.
  const breadcrumb = useMemo(() => {
    if (!page || siblings.length === 0) return [] as PageSummary[];
    const byId = new Map(siblings.map((p) => [p.id, p]));
    const chain: PageSummary[] = [];
    let current = page.parent_id ? byId.get(page.parent_id) : undefined;
    while (current) {
      chain.unshift(current);
      current = current.parent_id ? byId.get(current.parent_id) : undefined;
    }
    return chain;
  }, [page, siblings]);

  if (isLoading || !page) return <p style={{ color: "var(--mute)" }}>Loading…</p>;

  const editor = memberById.get(page.last_edited_by_id);

  return (
    <article style={{ maxWidth: 800, margin: "0 auto" }}>
      <nav style={breadcrumbNav}>
        <Link to="/wiki" style={crumbLink}>
          Wiki
        </Link>
        {space && (
          <>
            <span style={crumbSep}>/</span>
            <Link to={`/wiki/spaces/${space.slug}`} style={crumbLink}>
              {space.name}
            </Link>
          </>
        )}
        {breadcrumb.map((b) => (
          <span key={b.id} style={{ display: "inline-flex", alignItems: "center" }}>
            <span style={crumbSep}>/</span>
            <Link to={`/wiki/pages/${b.id}`} style={crumbLink}>
              {b.title}
            </Link>
          </span>
        ))}
      </nav>

      <header style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 4 }}>
        <h1
          style={{
            flex: 1,
            fontSize: 36,
            fontWeight: 600,
            letterSpacing: "-0.4px",
            margin: 0,
            fontFamily: "var(--font-serif), Georgia, serif",
          }}
        >
          {page.title}
        </h1>
        <button type="button" onClick={() => setSubPageModalOpen(true)} style={subPageBtn}>
          + Add sub-page
        </button>
      </header>

      <p style={{ color: "var(--mute)", fontSize: 12, marginBottom: 32 }}>
        Last edited by {editor?.display_name ?? "—"} · v{page.current_version} ·{" "}
        {new Date(page.updated_at).toLocaleString()}
      </p>

      <div
        style={{
          fontFamily: "var(--font-serif), Georgia, serif",
          fontSize: 17,
          lineHeight: 1.7,
          color: "var(--ink)",
        }}
        className="m2s-markdown"
      >
        {page.body_md.trim() ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.body_md}</ReactMarkdown>
        ) : (
          <p style={{ color: "var(--mute)", fontStyle: "italic" }}>
            This page is empty. Add a sub-page below or edit the body.
          </p>
        )}
      </div>

      <PageAttachments pageId={page.id} />

      <CreatePageModal
        open={subPageModalOpen}
        onClose={() => setSubPageModalOpen(false)}
        spaceId={page.space_id}
        spaceSlug={space?.slug ?? null}
        parentId={page.id}
        parentTitle={page.title}
      />
    </article>
  );
}

const breadcrumbNav: CSSProperties = {
  marginBottom: 12,
  fontSize: 12,
  color: "var(--mute)",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 4,
};

const crumbLink: CSSProperties = {
  color: "var(--body-mid)",
  textDecoration: "none",
};

const crumbSep: CSSProperties = {
  margin: "0 4px",
  color: "var(--mute)",
};

const subPageBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12.5,
  color: "var(--ink)",
  cursor: "pointer",
  fontWeight: 500,
  flexShrink: 0,
};
